/** クロップ座標データ（react-easy-crop の croppedAreaPixels に対応） */
export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * 選んだ比率（幅 / 高さ）。null は「そのまま」（元画像全体）。
   * 再クロップ時に比率を復元するために持つ。古いデータでは省略される。
   */
  aspect?: number | null;
}

/** ImageEditorModal の確定時に返す結果 */
export interface CropResult {
  file: File;
  base64: string;
  cropData: CropData;
}

export const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");

    // data URL 以外（S3等）はキャッシュバスティングを付与。
    // Cropper が crossOrigin なしで先に読み込んだキャッシュと衝突するのを回避。
    if (url.startsWith("data:")) {
      image.src = url;
    } else {
      image.src = url + (url.includes("?") ? "&" : "?") + "_cb=" + Date.now();
    }
  });
};

/**
 * Canvas API で画像をクロップし、base64 文字列を返す
 */
export const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: CropData,
  format: string = "image/jpeg",
  quality: number = 0.92,
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context is not available");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return canvas.toDataURL(format, quality);
};

/**
 * 画像をリサイズし base64 文字列を返す
 */
export const resizeImage = async (
  imageSrc: string,
  targetWidth: number,
  targetHeight: number,
  format: string = "image/jpeg",
  quality: number = 0.9,
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context is not available");

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  return canvas.toDataURL(format, quality);
};

/** Blob を dataURL に変換する */
export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** URL の画像を Blob で取得する */
export const fetchImageBlob = async (url: string): Promise<Blob> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);
  return res.blob();
};

/**
 * URL の画像を取得して dataURL に変換する。
 * Canvas で getImageData する処理（透過トリム等）は同一オリジンが必要なため、
 * S3 の URL は先にこれで dataURL 化してから渡す。
 */
export const urlToDataUrl = async (url: string): Promise<string> =>
  blobToDataUrl(await fetchImageBlob(url));

/**
 * base64 文字列を File オブジェクトに変換する
 */
export const base64ToFile = (
  base64: string,
  fileName: string,
  mimeType: string = "image/jpeg",
): File => {
  const byteString = atob(base64.split(",")[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], fileName, { type: mimeType });
};

/**
 * 透過 PNG の余白（アルファがほぼ 0 の縁）を切り詰めた PNG dataURL を返す。
 *
 * AI 生成のプロジェクトロゴはキャンバス中央に余白付きで描かれるため、
 * 実ロゴ部分だけを取り出してアスペクト比を最適化する用途で使う。
 * `paddingRatio` は切り詰め後の長辺に対する残す余白の割合。
 *
 * 注意: getImageData を使うため imageSrc は dataURL（または同一オリジン）で
 * 渡すこと。S3 URL は先に fetch → dataURL 化してから渡す。
 */
export const trimTransparentEdges = async (
  imageSrc: string,
  alphaThreshold: number = 8,
  paddingRatio: number = 0.04,
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context is not available");

  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  ctx.drawImage(image, 0, 0);

  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > alphaThreshold) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  // 全ピクセル透過（想定外）の場合はそのまま返す
  if (maxX < minX || maxY < minY) {
    return canvas.toDataURL("image/png");
  }

  const pad = Math.round(Math.max(maxX - minX, maxY - minY) * paddingRatio);
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);

  const outWidth = maxX - minX + 1;
  const outHeight = maxY - minY + 1;
  const output = document.createElement("canvas");
  output.width = outWidth;
  output.height = outHeight;
  const outputCtx = output.getContext("2d");
  if (!outputCtx) throw new Error("Canvas 2D context is not available");
  outputCtx.drawImage(canvas, minX, minY, outWidth, outHeight, 0, 0, outWidth, outHeight);
  return output.toDataURL("image/png");
};
